import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { LayoutDashboard, Users, ClipboardList, CalendarRange, Settings, MonitorSmartphone, PartyPopper, HeartPulse, LogOut, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  ["/admin", "Dashboard", LayoutDashboard, "dashboard"],
  ["/admin/workers", "Workers", Users, "workers"],
  ["/admin/records", "Records", ClipboardList, "records"],
  ["/admin/summary", "Monthly Summary", CalendarRange, "summary"],
  ["/admin/holidays", "Holidays", PartyPopper, "holidays"],
  ["/admin/sick-leave", "Sick Leave", HeartPulse, "sick-leave"],
  ["/admin/settings", "Settings", Settings, "settings"],
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
    isActive ? "bg-emerald-500 text-slate-950" : "text-slate-300 hover:bg-slate-800 hover:text-white"
  }`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [company, setCompany] = useState("");
  useEffect(() => { api.get("/public/info").then((r) => setCompany(r.data.company_name)).catch(() => {}); }, []);
  if (user === null)
    return <div className="h-screen grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50">
      <aside className="md:w-64 bg-slate-900 text-slate-100 md:h-screen md:sticky md:top-0 flex flex-col p-4 md:p-6 gap-6">
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] text-emerald-400 uppercase">Attendance OS</p>
          <h1 data-testid="admin-company-name" className="font-display text-lg font-extrabold tracking-tight leading-tight">{company}</h1>
        </div>
        <nav className="flex md:flex-col gap-1 overflow-x-auto">
          {NAV.map(([to, label, Icon, id]) => (
            <NavLink key={to} to={to} end={to === "/admin"} className={linkCls} data-testid={`nav-link-${id}`}>
              <Icon size={16} /> <span className="whitespace-nowrap">{label}</span>
            </NavLink>
          ))}
          <NavLink to="/" className={linkCls} data-testid="nav-link-kiosk">
            <MonitorSmartphone size={16} /> <span className="whitespace-nowrap">Open Kiosk</span>
          </NavLink>
        </nav>
        <div className="md:mt-auto flex items-center justify-between gap-2 border-t border-slate-800 pt-4">
          <span className="text-xs text-slate-400 truncate" data-testid="admin-user-email">{user.email}</span>
          <button onClick={logout} data-testid="admin-logout-btn" className="p-2 rounded-md hover:bg-slate-800 transition-colors" title="Log out">
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main className="flex-1 p-5 md:p-10 space-y-8 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
